import { db } from '../firebase';
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  serverTimestamp,
  updateDoc,
  doc,
  getFirestore
} from 'firebase/firestore';

// Get the auth instance from the existing Firebase app
import { app } from '../firebase';
const auth = getAuth(app);

// Create a Google Auth Provider
const googleProvider = new GoogleAuthProvider();

// Improved Google Sign-In function with better error handling
export const signInWithGoogle = async () => {
  try {
    console.log('Signing in with Google...');
    // Add additional scopes if needed
    googleProvider.addScope('profile');
    googleProvider.addScope('email');
    
    const result = await signInWithPopup(auth, googleProvider);
    console.log('Google sign-in successful:', result.user.email);
    return result.user;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    
    // Provide more specific error messages
    if (error.code === 'auth/configuration-not-found') {
      throw new Error('Authentication service is not properly configured. Please contact support.');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('Pop-up was blocked by your browser. Please allow pop-ups for this site.');
    } else if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in was cancelled. Please try again.');
    } else {
      throw error;
    }
  }
};

// Add a redirect-based sign-in method as fallback
export const signInWithGoogleRedirect = async () => {
  try {
    console.log('Signing in with Google (redirect)...');
    await signInWithRedirect(auth, googleProvider);
    // Note: This function will redirect the page, so no return value
  } catch (error) {
    console.error('Error with Google redirect sign-in:', error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    console.log('Signing out user');
    await signOut(auth);
    console.log('User signed out successfully');
    return true;
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Calculation history functions
export const saveCalculationToHistory = async (userEmail, calculationData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      console.error('No authenticated user found');
      throw new Error('You must be logged in to save calculations');
    }
    
    console.log('Current user:', auth.currentUser.uid);
    console.log('Saving calculation for user:', userEmail);
    
    const calculationRef = collection(db, "calculationHistory");
    const docRef = await addDoc(calculationRef, {
      ...calculationData,
      userEmail: userEmail,
      userId: auth.currentUser.uid,
      timestamp: serverTimestamp()
    });
    
    console.log('Calculation saved with ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error saving calculation:', error);
    throw error;
  }
};

export const getUserCalculationHistory = async (userEmail) => {
  try {
    console.log(`Fetching history for user: ${userEmail}`);
    
    // Create a reference to the calculationHistory collection
    const historyRef = collection(db, 'calculationHistory');
    
    // Create a query against the collection
    const q = query(
      historyRef, 
      where('userEmail', '==', userEmail),
      orderBy('timestamp', 'desc')
    );
    
    console.log('Query created, executing...');
    
    // Execute the query
    const querySnapshot = await getDocs(q);
    const history = [];
    
    console.log(`Query executed, got ${querySnapshot.size} results`);
    
    // Process the results
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      console.log(`Processing document: ${doc.id}`, data);
      
      history.push({
        id: doc.id,
        ...data,
        // Convert Firestore timestamp to JS Date if needed
        timestamp: data.timestamp?.toDate?.() || data.timestamp
      });
    });
    
    console.log(`Processed ${history.length} history items for ${userEmail}`);
    return history;
  } catch (error) {
    console.error('Error getting calculation history:', error);
    // Add more detailed error information
    if (error.code) {
      console.error(`Firebase error code: ${error.code}`);
    }
    throw new Error(`Failed to load history: ${error.message}`);
  }
};

// Email link authentication settings
const actionCodeSettings = {
  // URL you want to redirect back to after sign-in
  url: window.location.origin,
  // This must be true
  handleCodeInApp: true,
  // Add your domains
  dynamicLinkDomain: 'remfee.com'
};

// Send sign-in link to email
export const sendSignInLink = async (email) => {
  try {
    console.log('Sending sign-in link to:', email);
    
    // Save the email locally so you don't need to ask the user for it again
    localStorage.setItem('emailForSignIn', email);
    
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    console.log('Email link sent successfully');
    return true;
  } catch (error) {
    console.error('Error sending email link:', error);
    throw error;
  }
};

// Improve the completeSignInWithEmailLink function with better error handling
export const completeSignInWithEmailLink = async (email, link) => {
  try {
    console.log('Completing sign-in with email link for:', email);
    
    // Add a timeout to the authentication request
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Authentication request timed out')), 15000);
    });
    
    // Race the auth request against the timeout
    const result = await Promise.race([
      signInWithEmailLink(auth, email, link),
      timeoutPromise
    ]);
    
    // Clear email from storage
    localStorage.removeItem('emailForSignIn');
    
    console.log('Email link sign-in successful:', result.user.email);
    return result.user;
  } catch (error) {
    console.error('Error completing email link sign-in:', error);
    
    // Provide more specific error messages
    if (error.code === 'auth/network-request-failed') {
      throw new Error('Network connection issue. Please check your internet connection and try again.');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('The email address is not valid.');
    } else if (error.code === 'auth/expired-action-code') {
      throw new Error('The sign-in link has expired. Please request a new one.');
    } else if (error.code === 'auth/invalid-action-code') {
      throw new Error('The sign-in link is invalid. Please request a new one.');
    } else {
      throw error;
    }
  }
};

// Improve the checkForEmailSignIn function with better error handling
export const checkForEmailSignIn = async () => {
  try {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = localStorage.getItem('emailForSignIn');
      
      // If missing email, prompt user for it
      if (!email) {
        email = window.prompt('Please provide your email for confirmation');
        
        if (!email) {
          throw new Error('Email is required to complete sign-in');
        }
      }
      
      console.log('Attempting to sign in with email link...');
      return await completeSignInWithEmailLink(email, window.location.href);
    }
    
    return null;
  } catch (error) {
    console.error('Error in checkForEmailSignIn:', error);
    throw error;
  }
};

// Add auth state change listener
export const onAuthStateChanged = (callback) => {
  return auth.onAuthStateChanged(callback);
};

// Add a retry mechanism for email link authentication
export const retrySignInWithEmailLink = async (email, link, maxRetries = 3) => {
  let lastError = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Attempt ${attempt} to sign in with email link...`);
      const result = await signInWithEmailLink(auth, email, link);
      console.log('Email link sign-in successful on retry:', result.user.email);
      return result.user;
    } catch (error) {
      console.error(`Attempt ${attempt} failed:`, error);
      lastError = error;
      
      // Only retry on network errors
      if (error.code !== 'auth/network-request-failed') {
        break;
      }
      
      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries) {
        const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
        console.log(`Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // If we get here, all retries failed
  throw lastError;
};

export const saveEmailToDatabase = async (emailData) => {
  console.log('Starting saveEmailToDatabase with data:', emailData);
  
  try {
    // Get Firestore instance
    const db = getFirestore();
    console.log('Firestore instance obtained');
    
    // Reference to emails collection
    const emailsCollection = collection(db, 'emails');
    console.log('Collection reference created');
    
    // Check if email already exists
    console.log('Checking if email exists:', emailData.email);
    const emailQuery = query(emailsCollection, where('email', '==', emailData.email));
    const existingEmails = await getDocs(emailQuery);
    
    console.log('Query executed, found:', existingEmails.empty ? 'no existing email' : 'existing email');
    
    if (existingEmails.empty) {
      // Email doesn't exist, add it
      console.log('Adding new email to database');
      const docRef = await addDoc(emailsCollection, emailData);
      console.log('Email saved to database with ID:', docRef.id);
    } else {
      // Email exists, update the timestamp
      console.log('Updating existing email record');
      const emailDoc = existingEmails.docs[0];
      await updateDoc(doc(db, 'emails', emailDoc.id), {
        lastSeen: new Date().toISOString(),
        // Update other fields if provided
        ...(emailData.name && { name: emailData.name }),
        ...(emailData.amazonStore && { amazonStore: emailData.amazonStore })
      });
      console.log('Email record updated with ID:', emailDoc.id);
    }
    
    return true;
  } catch (error) {
    console.error('Error in saveEmailToDatabase:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    throw error;
  }
}; 